import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/errors';
import type { PortalRole, RegisterInput, Session } from '@/types';

const SESSION_TOKEN_KEY = 'voicera:session:token';

type AuthContextValue = {
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<Session>;
  register: (input: RegisterInput) => Promise<Session>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

/** Session provider — token stored in localStorage, validated via API. */
export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) {
      setSession(null);
      return;
    }

    try {
      const next = await authApi.getSession();
      setSession(next);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession(null);
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    }
  }, []);

  useEffect(() => {
    void (async () => {
      setIsLoading(true);
      await refreshSession();
      setIsLoading(false);
    })();
  }, [refreshSession]);

  const login = useCallback(async (identifier: string, password: string) => {
    const response = await authApi.login({ identifier, password });
    setSession(response.session);
    localStorage.setItem(SESSION_TOKEN_KEY, response.token ?? '');
    return response.session;
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const response = await authApi.register(input);
    setSession(response.session);
    localStorage.setItem(SESSION_TOKEN_KEY, response.token ?? '');
    return response.session;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setSession(null);
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: !!session,
      isLoading,
      login,
      register,
      logout,
      refreshSession,
    }),
    [session, isLoading, login, register, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useRole(): PortalRole {
  return useAuth().session?.user.role ?? 'USER';
}

export function useIsPlatformAdmin(): boolean {
  const { session } = useAuth();
  return session?.is_platform_admin ?? session?.user.role === 'VOICERA_ADMIN';
}

/** Default post-login destination based on role / tenant. */
export function homePathForSession(session: Session | null | undefined): string {
  if (!session) return '/login';
  if (session.user.role === 'VOICERA_ADMIN' && !session.tenant) {
    return '/platform/dashboard';
  }
  return '/dashboard';
}
